import React, { useEffect, useState, useCallback } from "react";
import { CCard, CCardBody, CCardHeader, CButton, CSpinner, CBadge } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilBolt } from "@coreui/icons";
import { getWarehouseInsight } from "../../api/hermesApi";
import ErrorAlert from "../UI/Status/ErrorAlert";

/**
 * Same button-triggered, never-auto-fetch UX as HermesPanel/WaiterInsightPanel —
 * GigaChat calls cost tokens. Unlike those, the period isn't picked by the user:
 * the server always analyzes a rolling last-7-days window (see hermesService.getWarehouseInsight).
 */
export default function WarehouseInsightPanel({ serverId }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requested, setRequested] = useState(false);

  const fetchInsight = useCallback(
    (force) => {
      if (!serverId) return;
      setRequested(true);
      setLoading(true);
      setError(null);
      getWarehouseInsight({ id: serverId, force })
        .then(setInsight)
        .catch(setError)
        .finally(() => setLoading(false));
    },
    [serverId],
  );

  useEffect(() => {
    setInsight(null);
    setError(null);
    setRequested(false);
  }, [serverId]);

  if (!serverId) return null;
  const content = insight?.content;

  return (
    <CCard className="mb-4">
      <CCardHeader className="d-flex align-items-center justify-content-between py-2">
        <div className="d-flex align-items-center gap-2">
          <CIcon icon={cilBolt} className="text-warning" />
          <span className="fw-semibold">AI-сводка по складу за неделю</span>
          {insight?.cached && (
            <CBadge color="secondary" title={insight.generated_at}>
              кэш
            </CBadge>
          )}
        </div>
        <CButton
          size="sm"
          color="primary"
          variant="outline"
          onClick={() => fetchInsight(requested)}
          disabled={loading}
        >
          {loading ? <CSpinner size="sm" /> : requested ? "Обновить сводку" : "Сформировать сводку"}
        </CButton>
      </CCardHeader>
      <CCardBody className="pt-3">
        <ErrorAlert error={error} title="Не удалось получить сводку" />

        {insight?.date_from && insight?.date_to && (
          <div className="small text-body-secondary mb-3">
            Остатки и расход — на сейчас, инвентаризации — за последние несколько месяцев. Период отчёта:{" "}
            {insight.date_from} — {insight.date_to}.
          </div>
        )}

        {!requested && !loading && (
          <div className="text-body-secondary py-2">
            Нажмите «Сформировать сводку», чтобы получить AI-разбор аномалий остатков, рисков закупки и
            результатов инвентаризаций.
          </div>
        )}

        {loading && !content && (
          <div className="d-flex align-items-center gap-2 text-body-secondary py-2">
            <CSpinner size="sm" />
            Анализируем склад…
          </div>
        )}

        {content && (
          <>
            <p className="mb-3">{content.summary}</p>

            {content.highlights?.length > 0 && (
              <div className="mb-3">
                <div className="small fw-semibold text-body-secondary mb-1">Наблюдения</div>
                <ul className="mb-0 ps-3">
                  {content.highlights.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </div>
            )}

            {content.risks?.length > 0 && (
              <div className="mb-3">
                <div className="small fw-semibold text-danger mb-1">Риски</div>
                <ul className="mb-0 ps-3">
                  {content.risks.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}

            {content.recommendations?.length > 0 && (
              <div>
                <div className="small fw-semibold text-success mb-1">Рекомендации</div>
                <ul className="mb-0 ps-3">
                  {content.recommendations.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CCardBody>
    </CCard>
  );
}
